import { useEffect, useState, type ReactNode } from "react"
import styled from "styled-components"
import { T } from "../adminTheme"
import { Callout, Check, Disclosure, Fields, Muted, MultiSelect, Page, Panel, Row, Table } from "./adminUi"
import Button from "@ui/button/Button"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { systemPost, systemErrorMessage } from "src/services/superAdminApi"
import type { SystemTransactionFilters } from "src/services/systemTransactions"

interface Amount { count: number; cents: number }
interface Compared extends Amount { trx_count: number; trx_cents: number; diff_cents: number }
export interface TurnoverFigures {
	deposits: Amount
	deposits_other: Amount
	inflows: Record<string, Amount>
	service_fees: Amount
	refunds: Amount
	card_spend: { settled: Compared; pending: Amount; purchases_trx: Amount; merchant_refunds_trx: Amount }
	issuer_fees: {
		card_price: Compared; auth_fee: Compared; cross_border: Compared; decline_fee: Compared; other: Compared
		trx_corrections: Compared; total: Compared; cross_border_reserved: Amount; trx_only: Amount; taken: Amount
	}
}
interface TurnoverGroup { companies: number; companies_with_deviation: number; figures: TurnoverFigures }
interface TurnoverIssuer { issuer: number; issuer_label: string; provider_code: string; cards: number; cards_with_ledger: number; complete: boolean; errors: string[]; figures: TurnoverFigures }
interface TurnoverCompany {
	company_uid: string; company_name: string; company_type: string; company_status: string; issuers: string[]
	complete: boolean; has_deviation: boolean; figures: TurnoverFigures; by_issuer: TurnoverIssuer[]
}
export interface TurnoverReport {
	generated_at: string
	period: { from: string | null; to: string | null }
	complete: boolean
	errors: string[]
	elapsed_ms: number
	companies_scanned: number
	companies_idle: number
	not_found: string[]
	skipped: unknown[]
	failed: unknown[]
	total: TurnoverGroup
	by_type: { company: TurnoverGroup; private: TurnoverGroup }
	companies: TurnoverCompany[]
	csv: string
}

const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px;`
const list = (items: string[]) => <ul>{items.map((text, index) => <li key={index}>{text}</li>)}</ul>

const money = (cents = 0) => formatCurrencyWithNumberToSymbolWithNumber(cents, "USD")
const red = T.danger

/** Wording for `diff_cents = trx − issuer`. */
export const feeDiffText = (diff = 0) => diff === 0 ? "Matches TRX" : `TRX shows ${money(Math.abs(diff))} ${diff < 0 ? "less" : "more"}`

const pairLabel = (pair: unknown) => {
	if (typeof pair === "string") return pair
	const p = (pair ?? {}) as Record<string, unknown>
	const parts = [p.company_name ?? p.company_uid, p.issuer_label ?? p.issuer, p.reason ?? p.error].filter(value => value !== undefined && value !== null && value !== "")
	return parts.length ? parts.join(" · ") : JSON.stringify(pair)
}

export const turnoverFileName = (type: string, from: string, to: string) =>
	`turnover_${type || "all"}_${from || "start"}_${to || new Date().toISOString().slice(0, 10)}.csv`

const downloadCsv = (csv: string, fileName: string) => {
	const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }))
	const link = document.createElement("a")
	link.href = url
	link.download = fileName
	link.click()
	URL.revokeObjectURL(url)
}

const Tile = ({ title, value, children }: { title: string; value: string; children?: ReactNode }) =>
	<Panel><Muted style={{ fontSize: 13 }}>{title}</Muted><div style={{ fontSize: 24, fontWeight: 650, margin: "4px 0 8px", fontVariantNumeric: "tabular-nums" }}>{value}</div><div style={{ fontSize: 13 }}>{children}</div></Panel>

const Headline = ({ figures, showInflows }: { figures: TurnoverFigures; showInflows?: boolean }) => {
	const diff = figures.issuer_fees.total.diff_cents
	return <Grid>
		<Tile title="Deposited to accounts" value={money(figures.deposits.cents)}>
			<Muted>Other external inflows: {money(figures.deposits_other.cents)}</Muted>
			{showInflows && <div style={{ marginTop: 8 }}><Disclosure flat label="All inflows by account and type">
				<Table><thead><tr><th>Account.type</th><th>Count</th><th>Amount</th></tr></thead>
					<tbody>{Object.entries(figures.inflows).map(([key, value]) => <tr key={key}><td><code>{key}</code></td><td>{value.count}</td><td>{money(value.cents)}</td></tr>)}</tbody>
				</Table>
			</Disclosure></div>}
		</Tile>
		<Tile title="Spent on cards" value={money(figures.card_spend.purchases_trx.cents)}>
			<Muted>Merchant refunds: {money(figures.card_spend.merchant_refunds_trx.cents)}</Muted>
		</Tile>
		<Tile title="Taken by issuer" value={money(figures.issuer_fees.taken.cents)}>
			<span style={{ color: diff ? red : T.muted, fontWeight: diff ? 600 : 400 }}>{feeDiffText(diff)}</span>
		</Tile>
	</Grid>
}

const feeRows = [["card_price", "Card price"], ["auth_fee", "Authorization fee"], ["cross_border", "Cross-border"], ["decline_fee", "Decline fee"], ["other", "Other charges"], ["trx_corrections", "TRX corrections (not in total)"], ["total", "Total"]] as const

const IssuerDetails = ({ row }: { row: TurnoverIssuer }) => {
	const fees = row.figures.issuer_fees
	const spend = row.figures.card_spend
	return <Panel title={row.issuer_label} hint={`${row.cards} cards, ${row.cards_with_ledger} with issuer ledger`}>
		{row.errors.length > 0 && <div style={{ color: red, marginTop: 6 }}>{row.errors.join("; ")}</div>}
		{row.cards_with_ledger > 0
			? <div style={{ marginTop: 6 }}>Settled purchases: issuer {money(spend.settled.cents)} · TRX {money(spend.settled.trx_cents)} · difference {money(spend.settled.diff_cents)} · pending {money(spend.pending.cents)}</div>
			: <div style={{ marginTop: 6 }}><Muted>No issuer ledger — card spend can't be compared with the issuer.</Muted></div>}
		<Table style={{ marginTop: 8 }}>
			<thead><tr><th>Issuer fee</th><th>Issuer</th><th>TRX</th><th>Difference</th></tr></thead>
			<tbody>{feeRows.map(([key, label]) => {
				const fee = fees[key]
				const isTotal = key === "total"
				return <tr key={key} style={isTotal ? { fontWeight: 700 } : undefined}>
					<td>{label}</td><td>{money(fee.cents)}</td><td>{money(fee.trx_cents)}</td>
					<td style={{ color: isTotal && fee.diff_cents ? red : T.muted }}>{money(fee.diff_cents)}</td>
				</tr>
			})}</tbody>
		</Table>
		<Muted style={{ display: "block", fontSize: 12.5, marginTop: 8 }}>Breakdown rows are for reference: TRX often books prepaid-card fees as “Other”. Only the total is a reliable comparison. Cross-border reserved on open holds: {money(fees.cross_border_reserved.cents)}. Taken from TRX only, without issuer check: {money(fees.trx_only.cents)}.</Muted>
	</Panel>
}

const CompanyRow = ({ row }: { row: TurnoverCompany }) => {
	const [open, setOpen] = useState(false)
	const f = row.figures
	const fees = f.issuer_fees.total
	return <>
		<tr>
			<td>{row.company_name}<small>{row.company_uid} · {row.issuers.join(", ")}</small>{!row.complete && <small style={{ color: red }}>Incomplete data — some reads failed</small>}</td>
			<td>{row.company_type}</td>
			<td>{money(f.deposits.cents)}</td>
			<td>{money(f.card_spend.purchases_trx.cents)}</td>
			<td>{money(f.issuer_fees.taken.cents)}<small>Issuer {money(fees.cents)} / TRX {money(fees.trx_cents)}</small><small style={fees.diff_cents ? { color: red } : undefined}>Difference {money(fees.diff_cents)}</small></td>
			<td>{money(f.service_fees.cents)}</td>
			<td>{row.has_deviation ? <strong style={{ color: red }}>Deviation</strong> : <Muted>OK</Muted>}</td>
			<td><Button title={open ? "Hide" : "Details"} variant="secondarySmall" onClick={() => setOpen(!open)} /></td>
		</tr>
		{open && <tr><td colSpan={8} style={{ background: T.surface2 }}><div style={{ display: "grid", gap: 10 }}>{row.by_issuer.map(issuer => <IssuerDetails key={issuer.issuer} row={issuer} />)}</div></td></tr>}
	</>
}

export const SuperAdminTurnover = ({ token }: { token: string }) => {
	const [options, setOptions] = useState<Pick<SystemTransactionFilters, "companies" | "issuers">>({ companies: [], issuers: [] })
	const [from, setFrom] = useState("")
	const [to, setTo] = useState("")
	const [type, setType] = useState("")
	const [companyUids, setCompanyUids] = useState<string[]>([])
	const [issuer, setIssuer] = useState("")
	const [includeIdle, setIncludeIdle] = useState(false)
	const [report, setReport] = useState<TurnoverReport>()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState("")

	useEffect(() => {
		let active = true
		void systemPost<SystemTransactionFilters>(token, "system/transactions/filters").then(result => {
			if (active && result.data) setOptions({ companies: result.data.companies ?? [], issuers: result.data.issuers ?? [] })
		})
		return () => { active = false }
	}, [token])

	const companies = options.companies.filter(company => !type || company.type === type)

	const run = async () => {
		setLoading(true)
		setError("")
		const body: Record<string, unknown> = {}
		if (from) body.from = from
		if (to) body.to = to
		if (type) body.type = type
		if (companyUids.length) body.company_uids = companyUids.join(",")
		if (issuer) body.issuer = Number(issuer)
		if (includeIdle) body.include_idle = true
		const result = await systemPost<TurnoverReport>(token, "system/turnover/report", body)
		setLoading(false)
		if (result.error) { setReport(undefined); setError(systemErrorMessage(result.error)) }
		else setReport(result.data)
	}

	const partialLedger = report?.companies.flatMap(company => company.by_issuer.filter(row => row.cards_with_ledger < row.cards).map(row => `${company.company_name} (${row.issuer_label}): ${row.cards_with_ledger} of ${row.cards}`)) ?? []

	return <Page>
		<Panel title="Report filters" hint="The issuer is read card by card: private accounts take about 15 s, all companies can take several minutes. Narrow by type or companies for a quick look.">
			<Fields>
				<label>From<input type="date" value={from} max={to || undefined} onChange={event => setFrom(event.target.value)} /></label>
				<label>To (inclusive)<input type="date" value={to} min={from || undefined} onChange={event => setTo(event.target.value)} /></label>
				<label>Account type<select value={type} onChange={event => { setType(event.target.value); setCompanyUids([]) }}>
					<option value="">All</option><option value="company">Companies</option><option value="private">Private accounts</option>
				</select></label>
				<label>Issuer<select value={issuer} onChange={event => setIssuer(event.target.value)}>
					<option value="">All issuers</option>
					{options.issuers.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
				</select></label>
				<label>Companies<MultiSelect label="Companies" allLabel="All companies" options={companies} value={companyUids} onChange={setCompanyUids} /></label>
			</Fields>
			<Row style={{ gap: 12, marginTop: 16 }}>
				<Check checked={includeIdle} onChange={event => setIncludeIdle(event.target.checked)}>Show companies without movements</Check>
				<Button title={loading ? "Building…" : "Build report"} disabled={loading} onClick={() => void run()} />
				{report && <Button title="Download CSV" variant="secondarySmall" onClick={() => downloadCsv(report.csv, turnoverFileName(type, from, to))} />}
			</Row>
		</Panel>

		{loading && <Callout tone="info" role="status">Building report… this can take a few minutes.</Callout>}
		{error && <Callout tone="danger">{error}</Callout>}

		{report && !loading && <>
			{!report.complete && <Callout title="Some data could not be read — figures are incomplete.">{list(report.errors)}</Callout>}
			{report.skipped.length > 0 && <Callout title="These issuers were not counted (no TRX comparison available), their figures are not in the totals:">{list(report.skipped.map(pairLabel))}</Callout>}
			{report.failed.length > 0 && <Callout tone="danger" title="Could not be read at all:">{list(report.failed.map(pairLabel))}</Callout>}
			{report.not_found.length > 0 && <Callout>Companies not found: {report.not_found.join(", ")}</Callout>}
			{partialLedger.length > 0 && <Callout tone="info" title="Card-spend comparison with the issuer is only possible for cards with an issuer ledger:">{list(partialLedger)}</Callout>}

			<Muted>{report.companies_scanned} companies scanned, {report.total.companies} with movements, {report.companies_idle} idle{report.total.companies_with_deviation ? `, ${report.total.companies_with_deviation} with deviation` : ""} · built in {Math.round(report.elapsed_ms / 1000)} s</Muted>
			<Headline figures={report.total.figures} showInflows />

			<Grid>{(["company", "private"] as const).map(key => <Panel key={key} title={key === "company" ? "Companies" : "Private accounts"} hint={`${report.by_type[key].companies} with movements`}>
				<div>Deposited: <strong>{money(report.by_type[key].figures.deposits.cents)}</strong></div>
				<div>Spent on cards: <strong>{money(report.by_type[key].figures.card_spend.purchases_trx.cents)}</strong></div>
				<div>Taken by issuer: <strong>{money(report.by_type[key].figures.issuer_fees.taken.cents)}</strong></div>
			</Panel>)}</Grid>

			<Panel title="Companies">
				<Table>
					<thead><tr>{["Company", "Type", "Deposited", "Spent on cards", "Taken by issuer", "Our fees", "Check", ""].map(label => <th key={label}>{label}</th>)}</tr></thead>
					<tbody>{report.companies.map(row => <CompanyRow key={row.company_uid} row={row} />)}</tbody>
				</Table>
				{report.companies.length === 0 && <Muted style={{ display: "block", padding: 16 }}>No companies with movements for this period.</Muted>}
			</Panel>
		</>}
	</Page>
}
